<template>
  <div class="min-h-screen bg-background">
    <div
      v-if="showAccountDeletedBanner"
      class="bg-green-50 border-b border-green-200 text-green-800 px-4 py-3 text-sm text-center"
    >
      <strong>{{ $t('home.accountDeletedBanner.title') }}</strong>
      <span class="ml-2">{{ $t('home.accountDeletedBanner.body') }}</span>
    </div>
    <div class="bg-primary/80 text-primary-foreground py-8">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 class="text-3xl md:text-4xl font-bold mb-2">Hai Afară!</h1>
        <p class="text-lg mb-4">{{ $t('home.subtitle') }}</p>
        <button
          class="px-4 py-2 bg-primary-foreground text-primary hover:bg-primary-foreground/90 rounded-md font-medium transition-colors"
          @click="handleGeolocation"
        >
          {{ $t('home.findNearMe') }}
        </button>
      </div>
    </div>

    <FeaturedTrailHero />

    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <h2 class="text-2xl font-bold mb-4 text-foreground">{{ $t('home.exploreZones') }}</h2>

      <div v-if="loading" class="text-center py-8">
        <div
          class="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"
        ></div>
        <p class="mt-4 text-muted-foreground">{{ $t('common.loadingZones') }}</p>
      </div>

      <div v-else-if="error" class="text-center py-8 text-destructive">
        {{ error }}
      </div>

      <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <router-link
          v-for="zone in zones"
          :key="zone.id"
          :to="localePath('Zone', { slug: zone.slug })"
          class="group bg-card rounded-xl border border-border shadow-sm hover:border-primary/30 transition-colors block p-4"
        >
          <div class="flex items-center mb-1">
            <h3
              class="text-lg font-semibold text-primary group-hover:text-primary/80 transition-colors truncate"
            >
              {{ zone.name }}
            </h3>
            <ChevronRight
              class="w-4 h-4 text-primary shrink-0 ml-auto group-hover:translate-x-0.5 transition-transform"
            />
          </div>
          <p v-if="zone.description" class="text-muted-foreground mb-3 line-clamp-3">
            {{ zone.description }}
          </p>
          <div class="flex gap-3 text-sm text-muted-foreground">
            <span>{{ $t('zones.trailCount', { count: zone.trails.length }) }}</span>
            <span>{{ $t('zones.poiCount', { count: zone.pois.length }) }}</span>
          </div>
        </router-link>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { ChevronRight } from 'lucide-vue-next'
import { useLocalePath } from '../composables/useLocalePath'
import { usePageTitle } from '../composables/usePageTitle'
import { useSeoTags } from '../composables/useSeoTags'
import { seoUrl } from '../utils/seoUrl'
import zonesApi from '../api/zones'
import FeaturedTrailHero from '../components/FeaturedTrailHero.vue'

const router = useRouter()
const route = useRoute()
const { t, locale } = useI18n()
usePageTitle(() => t('titles.home'), { brandFirst: true })
useSeoTags(() => ({
  title: 'Hai Afară!',
  url: seoUrl('Home', locale.value),
}))
const { localePath } = useLocalePath()

const zones = ref([])
const loading = ref(true)
const error = ref(null)
const showAccountDeletedBanner = ref(false)

const fetchZones = async () => {
  try {
    const response = await zonesApi.getAll()
    zones.value = response.data
  } catch (error_) {
    error.value = t('common.errorLoadingZones')
    console.error('Error fetching zones:', error_)
  } finally {
    loading.value = false
  }
}

const handleGeolocation = () => {
  if (navigator.geolocation) {
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords
        try {
          const response = await zonesApi.getNearest(latitude, longitude, 5)
          if (response.data.length > 0) {
            router.push(localePath('Zone', { slug: response.data[0].slug }))
          } else {
            alert(t('home.noZonesNearby'))
          }
        } catch (error_) {
          console.error('Error finding nearest zones:', error_)
          alert(t('home.errorFindingZones'))
        }
      },
      (err) => {
        console.error('Geolocation error:', err)
        alert(t('home.geolocationError'))
      },
    )
  } else {
    alert(t('home.geolocationUnsupported'))
  }
}

onMounted(() => {
  fetchZones()

  if (route.query.accountDeleted === '1') {
    showAccountDeletedBanner.value = true
    // Strip the query param from the URL without reloading
    setTimeout(() => {
      const rest = { ...route.query }
      delete rest.accountDeleted
      router.replace({ query: rest })
    }, 50)
  }
})
</script>
